import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { FiImage, FiLink, FiCode, FiType } from 'react-icons/fi';
import { BiBold, BiItalic, BiUnderline, BiHeading } from 'react-icons/bi';
import { BsBlockquoteLeft, BsCode, BsThreeDots } from 'react-icons/bs';
import { MdFormatListBulleted, MdFormatListNumbered } from 'react-icons/md';
import { generateClient } from 'aws-amplify/api';
import { uploadData, downloadData, getUrl } from 'aws-amplify/storage';
import { v4 as uuidv4 } from 'uuid';
import Header from '../components/Header';
import { PageContainer } from '../App';
import Snackbar from '../components/Snackbar';

const client = generateClient();

const ArticleDraftPage = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Get article ID from URL if editing
  const [showTextToolbar, setShowTextToolbar] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(!!id);
  const fileInputRef = useRef(null);
  const titleRef = useRef(null);
  const contentRef = useRef(null);

  // Load existing article content if editing
  useEffect(() => {
    const loadArticle = async () => {
      if (!id) return;

      try {
        const { data: article } = await client.models.Article.get({ id });
        if (!article) {
          setError('Article not found');
          return;
        }

        // Function to set content with retry
        const setContent = (retryCount = 0) => {
          if (retryCount > 10) {
            console.error('Failed to set title after multiple retries');
            return;
          }

          if (titleRef.current && contentRef.current) {
            titleRef.current.textContent = article.title || '';
            
            if (article.contentS3Url) {
              downloadData({
                path: article.contentS3Url
              }).result.then(async (downloadResult) => {
                const content = await downloadResult.body.text();
                
                // Create a temporary div to parse and modify the content
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = content || '';

                // Find all images with s3key and replace with signed URLs
                const images = tempDiv.getElementsByTagName('img');
                await Promise.all(Array.from(images).map(async (img) => {
                  const s3key = img.dataset.s3key;
                  if (s3key) {
                    try {
                      const { url } = await getUrl({ path: s3key });
                      img.src = url;
                    } catch (err) {
                      console.error('Error getting signed URL for image:', err);
                      // Keep the original src if there's an error
                    }
                  }
                }));

                // Set the processed content
                contentRef.current.innerHTML = tempDiv.innerHTML;
              });
            }
          } else {
            // Retry after a short delay
            setTimeout(() => setContent(retryCount + 1), 100);
          }
        };

        // Start setting content
        setContent();

      } catch (err) {
        console.error('Error loading article:', err);
        setError('Failed to load article');
      } finally {
        setIsLoading(false);
      }
    };

    loadArticle();
  }, [id]);

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
  };

  const handleFormat = (format) => {
    switch (format) {
      case 'bold':
        execCommand('bold');
        break;
      case 'italic':
        execCommand('italic');
        break;
      case 'underline':
        execCommand('underline');
        break;
      case 'heading':
        execCommand('formatBlock', '<h2>');
        break;
      case 'quote':
        execCommand('formatBlock', '<blockquote>');
        break;
      case 'code':
        execCommand('formatBlock', '<pre>');
        break;
      case 'ul':
        execCommand('insertUnorderedList');
        break;
      case 'ol':
        execCommand('insertOrderedList');
        break;
      case 'link':
        const url = prompt('Enter the URL:');
        if (url) execCommand('createLink', url);
        break;
      default:
        break;
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        // Generate unique key for the image
        const timestamp = new Date().getTime();
        const imageKey = `article-images/${timestamp}_${uuidv4()}.${file.name.split('.').pop()}`;
        
        // Upload to S3
        const uploadResult = await uploadData({
          path: imageKey,
          data: file,
          options: {
            contentType: file.type,
            metadata: {
              author: 'Amanjot Kaur'
            }
          }
        }).result;
        
        // Get temporary URL for display
        const { url } = await getUrl({ path: uploadResult.path });
        
        // Create image element with S3 key as data attribute
        const imgElement = document.createElement('img');
        imgElement.src = url;
        imgElement.style.maxWidth = '100%';
        imgElement.dataset.s3key = uploadResult.path;

        // Insert image at cursor position or append to content
        const selection = window.getSelection();
        const range = selection.getRangeAt(0);
        range.deleteContents();
        range.insertNode(imgElement);
        
        // Move cursor after the image
        range.setStartAfter(imgElement);
        range.setEndAfter(imgElement);
        selection.removeAllRanges();
        selection.addRange(range);
        
        // Add a line break after the image
        const br = document.createElement('br');
        range.insertNode(br);
        range.setStartAfter(br);
        range.setEndAfter(br);
        selection.removeAllRanges();
        selection.addRange(range);
      } catch (error) {
        console.error('Error uploading image:', error);
        setError('Failed to upload image. Please try again.');
      }
    }
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const toggleTextToolbar = () => {
    setShowTextToolbar(!showTextToolbar);
  };

  const handlePublish = async () => {
    const title = titleRef.current?.textContent?.trim();
    const content = contentRef.current?.innerHTML?.trim();

    if (!title) {
      setError('Please add a title to your article');
      return;
    }

    if (!content || content === '<br>') {
      setError('Please add some content to your article');
      return;
    }

    // Find the first image in the content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    const firstImage = tempDiv.querySelector('img');

    if (!firstImage) {
      setError('Please add at least one banner image to your article');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Use the first image's S3 key as banner image
      const bannerImageUrl = firstImage.dataset.s3key;

      // Upload article content as is (with S3 keys in img tags)
      const contentBlob = new Blob([content], { type: 'text/html;charset=utf-8' });
      const contentKey = id ? 
        `article-contents/${id}.html` : 
        `article-contents/${uuidv4()}.html`;

      const uploadResult = await uploadData({
        path: contentKey,
        data: contentBlob,
        options: {
          contentType: 'text/html;charset=utf-8',
          metadata: {
            author: 'Amanjot Kaur',
            title: encodeURIComponent(title)
          }
        }
      }).result;

      const cleanPreview = content
        .replace(/<[^>]+>/g, '') // Remove all HTML tags
        .replace(/&nbsp;/g, ' ') // Replace &nbsp; with spaces
        .replace(/\s+/g, ' ') // Replace multiple spaces with single space
        .trim();

      const articleData = {
        title,
        content: cleanPreview,
        author: 'Amanjot Kaur',
        contentS3Url: uploadResult.path,
        bannerImageUrl,
        readTime: Math.ceil((content.replace(/<[^>]*>/g, '').split(' ').length) / 200)
      };

      let result;
      if (id) {
        result = await client.models.Article.update({
          id,
          ...articleData
        });
      } else {
        result = await client.models.Article.create(articleData);
      }

      const { data: article, errors } = result;
      if (errors) {
        throw new Error(errors[0].message);
      }

      navigate(`/article/${article.id}`);
    } catch (err) {
      console.error('Error publishing article:', err);
      setError(err.message || 'Failed to publish article. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <PageContainer>
        <DraftContent>
          <LoadingMessage>Loading article...</LoadingMessage>
        </DraftContent>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <DraftContent>
        <Header isDraftPage={true} onPublish={handlePublish} />
        
        <input 
          type="file" 
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept="image/*"
          onChange={handleImageUpload}
        />

        <Editor>
          <TitleInput
            ref={titleRef}
            placeholder="Title"
            contentEditable="true"
            suppressContentEditableWarning={true}
          />
          <ContentInput
            ref={contentRef}
            placeholder="Tell your story..."
            contentEditable="true"
            suppressContentEditableWarning={true}
          />
        </Editor>

        <SideToolbarContainer>
          <SideToolbar>
            <SideToolbarButton 
              onClick={toggleTextToolbar}
              active={showTextToolbar}
            >
              <FiType />
              <Tooltip>Text formatting</Tooltip>
            </SideToolbarButton>
            <SideToolbarButton onClick={handleImageClick}>
              <FiImage />
              <Tooltip>Add an image</Tooltip>
            </SideToolbarButton>
            <SideToolbarButton>
              <BsThreeDots />
              <Tooltip>More options</Tooltip>
            </SideToolbarButton>
          </SideToolbar>

          {showTextToolbar && (
            <TextFormatToolbar>
              <ToolbarButton onClick={() => handleFormat('heading')}>
                <BiHeading />
                <Tooltip>Heading</Tooltip>
              </ToolbarButton>
              <ToolbarButton onClick={() => handleFormat('bold')}>
                <BiBold />
                <Tooltip>Bold</Tooltip>
              </ToolbarButton>
              <ToolbarButton onClick={() => handleFormat('italic')}>
                <BiItalic />
                <Tooltip>Italic</Tooltip>
              </ToolbarButton>
              <ToolbarDivider />
              <ToolbarButton onClick={() => handleFormat('link')}>
                <FiLink />
                <Tooltip>Add a link</Tooltip>
              </ToolbarButton>
              <ToolbarButton onClick={() => handleFormat('code')}>
                <BsCode />
                <Tooltip>Add code</Tooltip>
              </ToolbarButton>
              <ToolbarDivider />
              <ToolbarButton onClick={() => handleFormat('ul')}>
                <MdFormatListBulleted />
                <Tooltip>Bulleted list</Tooltip>
              </ToolbarButton>
              <ToolbarButton onClick={() => handleFormat('ol')}>
                <MdFormatListNumbered />
                <Tooltip>Numbered list</Tooltip>
              </ToolbarButton>
            </TextFormatToolbar>
          )}
        </SideToolbarContainer>
      </DraftContent>
      {error && (
        <Snackbar 
          message={error} 
          onClose={() => setError(null)} 
          autoHideDuration={5000}
        />
      )}
    </PageContainer>
  );
};

const DraftContent = styled.div`
  position: relative;
  min-height: 100vh;
  background: white;
`;

const Editor = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 6rem 2rem 2rem;
`;

const TitleInput = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  border: none;
  outline: none;
  margin-bottom: 1rem;
  font-family: gt-super, Georgia, serif;

  &:empty:before {
    content: attr(placeholder);
    color: #757575;
  }
`;

const ContentInput = styled.div`
  font-size: 1.2rem;
  line-height: 1.6;
  outline: none;
  min-height: 400px;
  font-family: source-serif-pro, Georgia, serif;

  &:empty:before {
    content: attr(placeholder);
    color: #757575;
  }

  a {
    color: #0066cc;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Tooltip = styled.span`
  position: absolute;
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  margin-left: 1rem;
  background: #292929;
  color: white;
  padding: 0.5rem 0.8rem;
  border-radius: 4px;
  font-size: 0.8rem;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s ease;
  pointer-events: none;
  z-index: 1000;

  &:before {
    content: '';
    position: absolute;
    right: 100%;
    top: 50%;
    transform: translateY(-50%);
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
    border-right: 6px solid #292929;
  }
`;

const SideToolbarContainer = styled.div`
  position: fixed;
  left: 20px;
  top: 40%;
  transform: translateY(-50%);
  display: flex;
  align-items: flex-start;
  gap: 0;
`;

const SideToolbar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.5rem;
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const TextFormatToolbar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.5rem;
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-left: -1px;
  border-left: 1px solid #e6e6e6;
  position: absolute;
  left: 100%;
  top: 0;
`;

const SideToolbarButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.active ? '#1a8917' : '#757575'};
  padding: 0.5rem;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  &:hover {
    background: #f2f2f2;
    color: #292929;

    ${Tooltip} {
      opacity: 1;
      visibility: visible;
      margin-left: 0.8rem;
    }
  }

  ${props => props.active && `
    &:after {
      content: '';
      position: absolute;
      right: -0.5rem;
      top: 50%;
      transform: translateY(-50%);
      width: 0;
      height: 0;
      border-top: 6px solid transparent;
      border-bottom: 6px solid transparent;
      border-right: 6px solid white;
      filter: drop-shadow(1px 0 1px rgba(0, 0, 0, 0.1));
    }
  `}

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ToolbarButton = styled.button`
  background: none;
  border: none;
  color: #757575;
  padding: 0.5rem;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  &:hover {
    background: #f2f2f2;
    color: #292929;

    ${Tooltip} {
      opacity: 1;
      visibility: visible;
      margin-left: 0.8rem;
    }
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ToolbarDivider = styled.div`
  width: 100%;
  height: 1px;
  background: #e6e6e6;
  margin: 0.25rem 0;
`;

const ErrorMessage = styled.div`
  color: #e53935;
  margin-top: 1rem;
  font-size: 0.9rem;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 4rem 0;
  color: #757575;
  font-size: 1.2rem;
`;

export default ArticleDraftPage;